import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QRCodeLib from 'qrcode';
import { useLinks } from '../hooks/useLinks';
import { useDomains } from '../hooks/useDomains';
import supabaseService from '../services/supabaseService';
import { getDefaultDomain, buildShortUrl } from '../utils/domainUtils';

const EditLinkPage = () => {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const { updateLink, deleteLink } = useLinks();
  const { domains } = useDomains();

  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    originalUrl: '',
    customSlug: '',
    domainId: '',
    tags: '',
    expiresAt: '',
    password: '',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});

  // Load link data
  useEffect(() => {
    const loadLinkData = async () => {
      try {
        setLoading(true);
        console.log('📖 Loading link data for ID:', linkId);

        // Get link details
        const linkData = await supabaseService.getLinkById(linkId);
        if (!linkData) {
          console.error('❌ Link not found:', linkId);
          navigate('/admin/links');
          return;
        }

        console.log('✅ Link loaded:', linkData);
        setLink(linkData);

        // Set form data
        setFormData({
          title: linkData.title || '',
          description: linkData.description || '',
          originalUrl: linkData.originalUrl || '',
          customSlug: linkData.shortCode || '',
          domainId: linkData.domainId || '',
          tags: linkData.tags ? linkData.tags.join(', ') : '',
          expiresAt: linkData.expiresAt ? linkData.expiresAt.split('T')[0] : '',
          password: linkData.password || '',
          isActive: linkData.isActive
        });

        // Load analytics
        const analyticsData = await supabaseService.getLinkAnalytics(linkId, 30);
        setAnalytics(analyticsData);
        console.log('📊 Analytics loaded:', analyticsData);

      } catch (error) {
        console.error('❌ Error loading link:', error);
        navigate('/admin/links');
      } finally {
        setLoading(false);
      }
    };

    if (linkId) {
      loadLinkData();
    }
  }, [linkId, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear errors
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.originalUrl.trim()) {
      errors.originalUrl = 'URL is required';
    } else {
      try {
        new URL(formData.originalUrl);
      } catch {
        errors.originalUrl = 'Please enter a valid URL';
      }
    }

    if (formData.customSlug && formData.customSlug.length < 3) {
      errors.customSlug = 'Slug must be at least 3 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const updates = {
        title: formData.title,
        description: formData.description,
        original_url: formData.originalUrl,
        short_code: formData.customSlug,
        domain_id: formData.domainId || null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        expires_at: formData.expiresAt || null,
        password: formData.password || null,
        is_active: formData.isActive
      };

      await updateLink(linkId, updates);
      console.log('✅ Link updated successfully');

      // Navigate back to links list
      navigate('/admin/links');

    } catch (error) {
      console.error('❌ Error updating link:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this link? This action cannot be undone.')) {
      try {
        await deleteLink(linkId);
        navigate('/admin/links');
      } catch (error) {
        console.error('❌ Error deleting link:', error);
      }
    }
  };

  const generateQRCode = async () => {
    try {
      setGeneratingQR(true);
      console.log('🔄 Generating QR code for:', shortUrl);

      const dataUrl = await QRCodeLib.toDataURL(shortUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#1a1a1a',
          light: '#ffffff'
        }
      });

      setQrDataUrl(dataUrl);
      setShowQR(true);
      console.log('✅ QR code generated successfully');
    } catch (error) {
      console.error('❌ Error generating QR code:', error);
      alert('Failed to generate QR code');
    } finally {
      setGeneratingQR(false);
    }
  };

  const copyQRCode = async () => {
    if (!qrDataUrl) return;

    try {
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      alert('✅ QR code copied to clipboard!');
    } catch (err) {
      console.error('❌ Error copying QR code:', err);
      alert('❌ Could not copy QR code');
    }
  };

  const downloadQRCode = () => {
    if (!qrDataUrl) return;

    const downloadLink = document.createElement('a');
    downloadLink.download = `qr-${formData.customSlug || 'link'}.png`;
    downloadLink.href = qrDataUrl;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const selectedDomain = domains.find(d => d.id === formData.domainId);
  const defaultDomain = getDefaultDomain(domains);
  const domainName = selectedDomain ? selectedDomain.domain : defaultDomain;
  const shortUrl = buildShortUrl(formData.customSlug, domainName);

  if (loading) {
    return (
      <div className="edit-link-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <h2>Loading link details...</h2>
        </div>

        <style jsx>{`
          .edit-link-page {
            max-width: 1000px;
            margin: 0 auto;
            padding: 20px;
          }

          .loading-state {
            text-align: center;
            padding: 60px 20px;
          }

          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #f3f4f6;
            border-top: 4px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="edit-link-page">
      <div className="page-header">
        <div className="header-left">
          <button
            className="back-button"
            onClick={() => navigate('/admin/links')}
          >
            ← Back to Links
          </button>
          <div className="page-title">
            <h1>Edit Link</h1>
            <p>Manage your short link and view its performance</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/admin/links')}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={saving}
          >
            🗑️ Delete Link
          </button>
        </div>
      </div>

      <div className="edit-content">
        <div className="edit-form-section">
          <div className="section-card">
            <h3>📝 Link Details</h3>


            {/* Form Fields */}
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Destination URL *</label>
                <textarea
                  name="originalUrl"
                  value={formData.originalUrl}
                  onChange={handleInputChange}
                  className={`form-textarea ${formErrors.originalUrl ? 'error' : ''}`}
                  rows="3"
                />
                {formErrors.originalUrl && (
                  <span className="field-error">{formErrors.originalUrl}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Domain</label>
                  <select
                    name="domainId"
                    value={formData.domainId}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">{defaultDomain} (default)</option>
                    {domains.filter(d => d.isVerified).map(domain => (
                      <option key={domain.id} value={domain.id}>
                        {domain.domain}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Custom Slug *</label>
                  <input
                    name="customSlug"
                    value={formData.customSlug}
                    onChange={handleInputChange}
                    className={`form-input ${formErrors.customSlug ? 'error' : ''}`}
                  />
                  {formErrors.customSlug && (
                    <span className="field-error">{formErrors.customSlug}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Link title for easy identification"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows="2"
                  placeholder="Brief description of the link"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tags</label>
                  <input
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="marketing, campaign, social"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Expiration Date</label>
                  <input
                    name="expiresAt"
                    type="date"
                    value={formData.expiresAt}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="toggle-label">
                  <input
                    name="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="toggle-input"
                  />
                  <span className="toggle-slider"></span>
                  Link is active
                </label>
                <span className="field-hint">
                  Inactive links will not redirect and show an error page
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="preview-and-analytics">
          {/* Link Preview Section */}
          <div className="section-card">
            <h3>🔗 Link Preview</h3>

            <div className="current-url-display">
              <label className="form-label">Live Short URL</label>
              <div className="url-display">
                <code>{shortUrl}</code>
                <button
                  type="button"
                  className="copy-btn"
                  onClick={() => navigator.clipboard.writeText(shortUrl)}
                  title="Copy URL"
                >
                  📋
                </button>
              </div>
            </div>

            {/* Link Preview Card */}
            <div className="link-preview-card">
              {link?.imageUrl ? (
                <img src={link.imageUrl} alt="Link preview" className="preview-image" />
              ) : (
                <div className="preview-image-placeholder">
                  <span className="image-icon">🔗</span>
                </div>
              )}

              <div className="preview-content">
                <div className="preview-url">{shortUrl}</div>
                <div className="preview-title">
                  {formData.title || link?.title || 'No title'}
                </div>
                <div className="preview-description">
                  {formData.description || link?.description || 'No description'}
                </div>
              </div>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="section-card">
            <h3>📱 QR Code</h3>
            <div className="qr-preview">
              {!showQR ? (
                <div className="qr-placeholder">
                  <div className="qr-icon">📱</div>
                  <p>Generate QR code for {shortUrl}</p>
                  <button
                    className="generate-qr-btn"
                    onClick={generateQRCode}
                    disabled={generatingQR}
                  >
                    {generatingQR ? (
                      <>
                        <div className="btn-spinner"></div>
                        Generating...
                      </>
                    ) : (
                      '🔄 Generate QR Code'
                    )}
                  </button>
                </div>
              ) : (
                <div className="qr-display">
                  <img
                    src={qrDataUrl}
                    alt="QR Code"
                    className="qr-image"
                  />
                  <div className="qr-actions">
                    <button className="qr-action-btn" onClick={copyQRCode}>
                      📋 Copy QR Code
                    </button>
                    <button className="qr-action-btn" onClick={downloadQRCode}>
                      📥 Download PNG
                    </button>
                    <button
                      className="qr-action-btn secondary"
                      onClick={() => setShowQR(false)}
                    >
                      🔄 Generate New
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="section-card">
            <h3>📊 Link Statistics</h3>

            {analytics ? (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{link?.clickCount || 0}</div>
                  <div className="stat-label">Total Clicks</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{analytics.totalClicks || 0}</div>
                  <div className="stat-label">Tracked Clicks</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">
                    {link?.createdAt ? new Date(link.createdAt).toLocaleDateString() : 'Unknown'}
                  </div>
                  <div className="stat-label">Created</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">
                    {analytics.clicksByDate?.length > 0 ? 'Active' : 'No clicks yet'}
                  </div>
                  <div className="stat-label">Status</div>
                </div>
              </div>
            ) : (
              <div className="analytics-loading">
                <div className="spinner-small"></div>
                <p>Loading analytics...</p>
              </div>
            )}

            {analytics?.clicksByDate?.length > 0 && (
              <div className="recent-activity">
                <h4>Recent Activity</h4>
                <div className="activity-list">
                  {analytics.clicksByDate.slice(-7).map((day, index) => (
                    <div key={index} className="activity-item">
                      <span className="activity-date">{day.date}</span>
                      <span className="activity-clicks">{day.clicks} clicks</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="page-footer">
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/admin/links')}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="spinner"></span>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      <style jsx>{`
        .edit-link-page {
          max-width: 1000px;
          margin: 0 auto;
          padding: 20px;
          min-height: calc(100vh - 100px);
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e9ecef;
        }

        .header-left {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .back-button {
          background: none;
          border: none;
          color: #007bff;
          cursor: pointer;
          font-size: 14px;
          padding: 0;
          text-align: left;
        }

        .back-button:hover {
          text-decoration: underline;
        }

        .page-title h1 {
          margin: 0 0 5px 0;
          font-size: 28px;
          color: #333;
        }

        .page-title p {
          margin: 0;
          color: #6c757d;
          font-size: 16px;
        }

        .header-actions {
          display: flex;
          gap: 10px;
        }

        .edit-content {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 30px;
          margin-bottom: 30px;
        }

        .preview-and-analytics {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .link-preview-card {
          border: 1px solid #e9ecef;
          border-radius: 12px;
          overflow: hidden;
          background: white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin-top: 15px;
        }

        .preview-image {
          width: 100%;
          height: 120px;
          object-fit: cover;
        }

        .preview-image-placeholder {
          width: 100%;
          height: 120px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
        }

        .image-icon {
          font-size: 32px;
        }

        .preview-content {
          padding: 15px;
        }

        .preview-url {
          font-size: 12px;
          color: #007bff;
          margin-bottom: 8px;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .preview-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin-bottom: 6px;
          line-height: 1.3;
        }

        .preview-description {
          font-size: 14px;
          color: #6c757d;
          line-height: 1.4;
        }

        .qr-preview {
          text-align: center;
        }

        .qr-placeholder {
          background: #f9fafb;
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          padding: 30px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .qr-icon {
          font-size: 48px;
          color: #6b7280;
        }

        .qr-placeholder p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .generate-qr-btn {
          background: #667eea;
          color: white;
          border: none;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .generate-qr-btn:hover {
          background: #5a6fd8;
          transform: translateY(-1px);
        }

        .generate-qr-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }

        .qr-display {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }

        .qr-image {
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
          border: 1px solid #e5e7eb;
        }

        .qr-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .qr-action-btn {
          background: #667eea;
          color: white;
          border: none;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .qr-action-btn:hover {
          background: #5a6fd8;
          transform: translateY(-1px);
        }

        .qr-action-btn.secondary {
          background: #6c757d;
        }

        .qr-action-btn.secondary:hover {
          background: #545b62;
        }

        .section-card {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .section-card h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          color: #333;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .current-url-display {
          margin-bottom: 25px;
        }

        .url-display {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 12px;
        }

        .url-display code {
          flex: 1;
          background: none;
          color: #007bff;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 14px;
        }

        .copy-btn {
          background: #007bff;
          color: white;
          border: none;
          padding: 6px 8px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .copy-btn:hover {
          background: #0056b3;
        }

        .form-grid {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-weight: 600;
          color: #333;
          font-size: 14px;
        }

        .form-input,
        .form-textarea,
        .form-select {
          padding: 12px;
          border: 1px solid #ced4da;
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
        }

        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .form-input:focus,
        .form-textarea:focus,
        .form-select:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0,123,255,.25);
        }

        .toggle-label {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          font-size: 14px;
          color: #333;
          font-weight: 600;
        }

        .toggle-input {
          display: none;
        }

        .toggle-slider {
          width: 44px;
          height: 24px;
          background: #ccc;
          border-radius: 12px;
          position: relative;
          transition: background 0.3s;
        }

        .toggle-slider::before {
          content: '';
          position: absolute;
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 50%;
          top: 2px;
          left: 2px;
          transition: transform 0.3s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }

        .toggle-input:checked + .toggle-slider {
          background: #28a745;
        }

        .toggle-input:checked + .toggle-slider::before {
          transform: translateX(20px);
        }

        .field-hint {
          font-size: 12px;
          color: #6c757d;
          margin-top: 4px;
        }

        .field-error {
          color: #dc3545;
          font-size: 12px;
          margin-top: 4px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 15px;
          margin-bottom: 25px;
        }

        .stat-card {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
          border: 1px solid #e9ecef;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
          color: #333;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 12px;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .recent-activity h4 {
          margin: 0 0 15px 0;
          font-size: 16px;
          color: #333;
        }

        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .activity-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 12px;
          background: #f8f9fa;
          border-radius: 6px;
          font-size: 14px;
        }

        .activity-date {
          color: #6c757d;
        }

        .activity-clicks {
          color: #333;
          font-weight: 500;
        }

        .analytics-loading {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }

        .spinner-small {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f4f6;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 10px;
        }

        .page-footer {
          display: flex;
          justify-content: flex-end;
          gap: 15px;
          padding-top: 20px;
          border-top: 1px solid #e9ecef;
          margin-top: 30px;
        }

        .btn {
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #0056b3;
          transform: translateY(-1px);
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #545b62;
        }

        .btn-danger {
          background: #dc3545;
          color: white;
          font-size: 13px;
          padding: 10px 16px;
        }

        .btn-danger:hover:not(:disabled) {
          background: #c82333;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @media (max-width: 768px) {
          .edit-content {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .page-header {
            flex-direction: column;
            gap: 15px;
            align-items: stretch;
          }

          .header-actions {
            justify-content: flex-start;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
};

export default EditLinkPage;